/**
 * Insights' reads and history writes (R249; docs/specs/cloud/insights.md §6, §8;
 * 20261105180000_insights.sql). Every figure is `@teslyn/domain` insights.ts; these only fetch rows. The Ops functions
 * and the tables run as the caller, so RLS still filters every row.
 *
 * A failed read throws, and the page says so: nothing here turns a failure into an empty list, so a failure can never
 * show as a zero. QC results and contracts are the exception that proves it: each has its own error, because the rest
 * of the page stands without them, and the page says that part could not be read.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  HistoryJob,
  HistoryJobRow,
  InsightsContract,
  InsightsInput,
  InsightsLiveEntry,
  InsightsLiveLine,
  InsightsQcRow,
} from '@teslyn/domain';
import { loadOpsProjects, must, pages, plainOpsError, type Res } from '../ops/api.ts';

export interface InsightsLoaded {
  readonly input: InsightsInput;
  readonly contracts: readonly InsightsContract[] | null;
  readonly contractsError: string | null;
  readonly qcError: string | null;
}

const NOT_UPDATED =
  'Insights needs a database update that is not applied yet. Nothing is shown until it is, because the pilot and refly figures would be wrong.';

/** Before 20261105180000 the Ops functions answer without these columns: say so, never show a pilot as unknown. */
function updated<T extends object>(rows: readonly T[], column: string): void {
  if (rows.length > 0 && !(column in rows[0]!)) throw new Error(NOT_UPDATED);
}

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

async function loadQc(sb: SupabaseClient, tenantId: string): Promise<InsightsQcRow[]> {
  const select = <T>(columns: string, failedOnly: boolean): Promise<T[]> =>
    pages<T>((from, to) => {
      const q = sb.from('qc_line_result').select(columns).eq('tenant_id', tenantId).is('deleted_at', null);
      return (failedOnly ? q.eq('verdict', 'fail') : q).order('id').range(from, to) as unknown as PromiseLike<
        Res<T[]>
      >;
    });
  // the verdicts of every line; a line's tests, which are large, only where the Refly list's reason word needs them
  const [rows, fails] = await Promise.all([
    select<InsightsQcRow & { id: string }>('id, line_status_id, verdict, created_at', false),
    select<{ id: string; tests: InsightsQcRow['tests'] }>('id, tests', true),
  ]);
  const testsOf = new Map(fails.map((f) => [f.id, f.tests]));
  return rows.map(({ id, ...r }) => {
    const tests = testsOf.get(id);
    return tests ? { ...r, tests } : r;
  });
}

interface ContractRow {
  project_id: string | null;
  number: string;
  status: string;
  signed_at: string | null;
  created_at: string;
  est_duration_value: number | null;
  est_duration_unit: InsightsContract['est_duration_unit'];
}

async function loadContracts(sb: SupabaseClient, tenantId: string): Promise<InsightsContract[]> {
  return pages<ContractRow>(
    (from, to) =>
      sb
        .from('contract')
        .select(
          'project_id, number, status, signed_at, created_at, ' +
            'est_duration_value:snapshot->quote->quote->est_duration_value, ' +
            'est_duration_unit:snapshot->quote->quote->>est_duration_unit',
        )
        .eq('tenant_id', tenantId)
        .in('status', ['signed', 'closed'])
        .order('id')
        .range(from, to) as unknown as PromiseLike<Res<ContractRow[]>>,
  );
}

/** Every job's rows, the whole history at once: the page's ranges and groupings are all in the browser. */
export async function loadInsights(sb: SupabaseClient, tenantId: string): Promise<InsightsLoaded> {
  const args = { p_tenant: tenantId, p_from: null, p_to: null, p_projects: null };
  const qc = loadQc(sb, tenantId).then(
    (rows) => ({ rows, error: null }),
    (e: unknown) => ({ rows: null, error: message(e) }),
  );
  const contracts = loadContracts(sb, tenantId).then(
    (rows) => ({ rows, error: null }),
    (e: unknown) => ({ rows: null, error: message(e) }),
  );
  const [projects, aircraft, people, blocks, lines, entries, history, qcRead, contractsRead] =
    await Promise.all([
      loadOpsProjects(sb, tenantId),
      // deleted aircraft and people too: their jobs still name them
      pages<{ id: string; registration: string; type_name: string | null }>(
        (from, to) =>
          sb
            .from('aircraft')
            .select('id, registration, type_name')
            .eq('tenant_id', tenantId)
            .order('id')
            .range(from, to) as unknown as PromiseLike<
            Res<{ id: string; registration: string; type_name: string | null }[]>
          >,
      ),
      pages<{ id: string; name: string }>(
        (from, to) =>
          sb
            .from('person')
            .select('id, name')
            .eq('tenant_id', tenantId)
            .order('id')
            .range(from, to) as unknown as PromiseLike<Res<{ id: string; name: string }[]>>,
      ),
      pages<InsightsInput['blocks'][number]>(
        (from, to) =>
          sb
            .from('block')
            .select('project_id, trav_spacing_m')
            .eq('tenant_id', tenantId)
            .is('deleted_at', null)
            .order('id')
            .range(from, to) as unknown as PromiseLike<Res<InsightsInput['blocks'][number][]>>,
      ),
      pages<InsightsLiveLine>(
        (from, to) =>
          sb.rpc('ops_line_status', args).range(from, to) as unknown as PromiseLike<Res<InsightsLiveLine[]>>,
      ),
      pages<InsightsLiveEntry>(
        (from, to) =>
          sb.rpc('ops_daily_entries', args).range(from, to) as unknown as PromiseLike<
            Res<InsightsLiveEntry[]>
          >,
      ),
      loadHistoryJobs(sb, tenantId),
      qc,
      contracts,
    ]);
  updated(lines, 'flt_num');
  updated(entries, 'pilot_name');
  return {
    input: {
      projects,
      aircraft: aircraft.map((a) => ({
        id: a.id,
        registration: a.registration,
        typeName: a.type_name ?? '',
      })),
      people,
      blocks,
      lines,
      entries,
      history,
      qc: qcRead.rows,
    },
    contracts: contractsRead.rows,
    contractsError: contractsRead.error,
    qcError: qcRead.error,
  };
}

export async function loadHistoryJobs(sb: SupabaseClient, tenantId: string): Promise<HistoryJobRow[]> {
  return pages<HistoryJobRow>(
    (from, to) =>
      sb
        .from('history_job')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('number')
        .range(from, to) as unknown as PromiseLike<Res<HistoryJobRow[]>>,
  );
}

/** Add a job as history. The database refuses a job already in Teslyn, whatever this page last read. */
export async function addHistoryJob(sb: SupabaseClient, tenantId: string, job: HistoryJob): Promise<void> {
  const r = (await sb.rpc('history_job_add', {
    p_tenant: tenantId,
    p_number: job.number,
    p_title: job.title,
    p_client_name: job.client_name,
    p_file_name: job.file_name,
    p_first_date: job.first_date,
    p_last_date: job.last_date,
    p_data: job.data,
  })) as Res<unknown>;
  if (r.error) throw new Error(plainHistoryError(r.error));
}

export async function removeHistoryJob(sb: SupabaseClient, id: string): Promise<void> {
  must((await sb.rpc('history_job_remove', { p_id: id })) as Res<unknown>);
}

function plainHistoryError(error: { readonly message: string; readonly code?: string }): string {
  // the database's own words name the job: "Job F26048 is already a project in Teslyn."
  return error.code === '23505' || error.code === '42501' ? error.message : plainOpsError(error);
}
